import { Chip } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box } from "@mui/system";

import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import EditRule from "./EditRule";
import { FormDrawer } from "components/FormDrawer";
import { ListPageHeader } from "components/ListPageHeader";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column } from "components/Table";
import { useResourceList } from "hooks/useResourceList";
import { useDeleteDialog } from "hooks/useDeleteDialog";
import { useFormDrawer } from "hooks/useFormDrawer";
import StatusLabel from "components/StatusLabel";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

type columnsType = "order" | "name" | "apps" | "obfuscations" | "users" | "groups" | "status" | "createdat" | "updatedat";

const defaultColumns: Column<columnsType>[] = [
    { columnId: "order", width: "8%" },
    { columnId: "name", width: "20%" },
    { columnId: "apps", width: "16%" },
    { columnId: "obfuscations", width: "16%" },
    { columnId: "users", width: "16%" },
    { columnId: "groups", width: "16%" },
    { columnId: "status", width: "12%" },
    { columnId: "createdat", width: "18%" },
    { columnId: "updatedat", width: "18%" },
];

function toRule(x: any): RulesListStateItem {
    return {
        id: x.id,
        order: x.ruleOrder,
        name: x.name,
        active: x.enable,
        createdAt: fromUnix(x.createdAt),
        updatedAt: fromUnix(x.updatedAt),
        apps: x.apps.map((a: any) => a.name),
        obfuscations: x.obfuscations.map((o: any) => o.name),
        userFilters: x.userFilters,
        groupFilters: x.groupFilters,
    };
}

interface HeaderFilterSummary {
    condition: string
    filter: string
}

interface GroupFilterSummary extends HeaderFilterSummary {
    groupId?: string
    groupName?: string
    directoryName?: string
    groupHidden?: boolean
}

const groupConditionText: { [key: string]: string } = {
    equal: "equals",
    not_equal: "does not equal",
    contains: "contains",
    not_contains: "does not contain",
};

// An exact filter shows the group name the server resolved from groupId - or
// "Unknown group" when that group no longer exists at all. A hidden-but-still-
// resolved group renders with its real name, struck through by the caller. A
// pattern filter matches by name across every group and shows the pattern itself.
function groupFilterChipLabel(filter: GroupFilterSummary): string {
    const exact = filter.condition === "equal" || filter.condition === "not_equal";
    if (!exact) {
        return `Group ${groupConditionText[filter.condition]} "${filter.filter}"`;
    }
    if (filter.groupId && !filter.groupName) {
        return `Group ${groupConditionText[filter.condition]} Unknown group`;
    }
    const directory = filter.groupName && filter.directoryName ? ` (${filter.directoryName})` : "";
    return `Group ${groupConditionText[filter.condition]} "${filter.groupName || filter.filter}"${directory}`;
}

interface RulesListStateItem {
    id: string
    order: number
    name: string
    active: boolean
    createdAt: Date
    updatedAt: Date
    apps: string[]
    obfuscations: string[]
    userFilters: HeaderFilterSummary[]
    groupFilters: GroupFilterSummary[]
}

export default function Rules() {
    const notification = useNotification();
    const login = useLogin();
    const { canEditPolicy } = useRole();

    //rules are fetched in full: the rows are drag-reorderable, which paging
    //would break, and the sort order is the rule priority rather than a column
    const list = useResourceList<RulesListStateItem, columnsType>({
        url: "/api/rules",
        columnsKey: "rules_v2",
        defaultColumns,
        mapItem: toRule,
        searchable: true,
    });

    const drawer = useFormDrawer();
    const del = useDeleteDialog({
        url: "/api/rules",
        label: "rule",
        onDeleted: list.reload,
    });

    const setRuleEnabled = async (id: string, state: boolean) => {
        const req = login.GetAxios();

        if (!req) {
            return; //not logged in, nothing to do
        }

        try {
            await req.put(`/api/rules/${id}/enable`, {
                enable: state,
            });
            list.reload();
            notification.Display({
                type: "success",
                title: "Success",
                message: "Rule status updated successfully"
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Could not update rule status",
                type: "error",
            });
        }
    };

    const onRowOrderChanged = async (newList: RulesListStateItem[]) => {
        const req = login.GetAxios();

        if (!req) {
            return; //not logged in, nothing to do
        }

        //show the new order straight away so the drag does not snap back while
        //the request is in flight
        const previous = list.items;
        list.setItems(newList);

        try {
            await req.post("/api/rules/ruleorder", newList.map(x => x.id));
            list.reload();
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Could not change rule order",
                type: "error",
            });
            list.setItems(previous);
        }
    };

    const getHeaderFilterText = (filter: HeaderFilterSummary) => {
        const conditionText = {
            "equal": "equals",
            "not_equal": "does not equal",
            "contains": "contains",
            "not_contains": "does not contain",
        } as { [key: string]: string };

        return `${conditionText[filter.condition]} "${filter.filter}"`;
    };



    return <>
        <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
            <ListPageHeader
                title="Rules"
                search={list.search}
                onSearchChange={list.setSearch}
                searchAllFields={list.searchAllFields}
                onSearchAllFieldsChange={list.setSearchAllFields}
                actionId="new-rule-button"
                actionLabel="Add New Rule"
                // NS-753
                onAction={canEditPolicy ? drawer.add : undefined}
            />
            <DataTable
                {...list.tableProps}
                onRowOrderChanged={canEditPolicy ? onRowOrderChanged : undefined}
                columns={{
                    order: {
                        displayName: "Priority",
                        render: (x) => {
                            return x.order + 1;
                        },
                    },
                    name: {
                        displayName: "Name",
                        render: (x) => {
                            return x.name;
                        },
                    },
                    apps: {
                        displayName: "Apps",
                        render: (x) => {
                            return <ChipWrap direction="column">
                                {x.apps.map(y => <Chip label={y} variant="outlined" color="primary" size="small" />)}
                            </ChipWrap>;
                        },
                    },
                    obfuscations: {
                        displayName: "Obfuscations",
                        render: (x) => {
                            return <ChipWrap direction="column">
                                {x.obfuscations.map(y => <Chip label={y} variant="outlined" color="primary" size="small" />)}
                            </ChipWrap>;
                        },
                    },
                    users: {
                        displayName: "User Filters",
                        render: (x) => {
                            return <ChipWrap direction="column">
                                {x.userFilters.map(y => <Chip label={getHeaderFilterText(y)} variant="outlined" color="primary" size="small" />)}
                            </ChipWrap>;
                        },
                    },
                    groups: {
                        displayName: "Group Filters",
                        render: (x) => {
                            return <ChipWrap direction="column">
                                {x.groupFilters.map((y, index) => <Chip
                                    key={`${y.condition}:${y.groupId || y.filter}:${index}`}
                                    label={groupFilterChipLabel(y)}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    sx={{ textDecoration: y.groupHidden ? "line-through" : undefined }}
                                />)}
                            </ChipWrap>;
                        },
                    },
                    status: {
                        displayName: "Status",
                        render: (x) => {
                            return x.active
                                ? <StatusLabel label="Active" color="success" />
                                : <StatusLabel label="Inactive" color="error" />;
                        }
                    },
                    createdat: {
                        displayName: "Created At",
                        render: (x) => {
                            return formatDateTime(x.createdAt);
                        },
                    },
                    updatedat: {
                        displayName: "Updated At",
                        render: (x) => {
                            return formatDateTime(x.updatedAt);
                        },
                    },

                }}
                rowOptionsMenu={[
                    {
                        icon: <EditIcon className="edit-button" fontSize="small" />,
                        text: canEditPolicy ? "Edit Rule Details" : "View Rule Details",
                        onClick: (x) => {
                            drawer.edit(x.id);
                        }
                    },
                    {
                        isVisible: () => canEditPolicy,
                        icon: (x) => {
                            if(x.active) {
                                return <ToggleOffOutlinedIcon/>;
                            }
                            return <ToggleOnOutlinedIcon/>;
                        },
                        text: (x) => {
                            if(x.active) {
                                return "Turn Off";
                            } 
                            return "Turn On";
                        },
                        onClick: (x) => {
                            setRuleEnabled(x.id, !x.active);
                        }
                    },
                    {
                        icon: <DeleteIcon className="delete-button" fontSize="small" />,
                        text: "Delete",
                        isVisible: () => canEditPolicy,
                        onClick: (x) => {
                            del.ask(x.name, x.id);
                        }
                    },
                ]}
            />
        </Box>

        <ConfirmDialog {...del.dialogProps} title="Delete Rule" />

        <FormDrawer {...drawer.drawerProps}>
            <EditRule
                ruleId={drawer.editingId}
                onExit={drawer.close}
                onSave={() => { list.reload(); drawer.close(); }}
            />
        </FormDrawer>
    </>;
}
